import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import WhatsAppFloat from '../home/components/WhatsAppFloat';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/SEO';

export default function AutomationPage() {
  const { t } = useTranslation('navigation');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Automatisation & Digitalisation - Alpha Solutions | Transformation Digitale"
        description="Automatisez vos processus, connectez vos systèmes et exploitez l'IA pour votre entreprise. Workflow automation, intégration systèmes, business intelligence et outils IA."
        keywords="automatisation entreprise Cameroun, digitalisation processus, workflow automation, business intelligence, IA entreprise"
        canonicalPath="/automation"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Services d'Automatisation et Digitalisation",
          "description": "Automatisation des processus, intégration systèmes, business intelligence et outils IA pour entreprises africaines",
          "provider": { "@type": "Organization", "name": "Alpha Solutions" },
          "areaServed": { "@type": "Country", "name": "Cameroun" }
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Libérez Votre Équipe des Tâches Répétitives
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Alpha Solutions automatise vos processus, connecte vos systèmes et vous donne les données nécessaires pour prendre de meilleures décisions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/237693385267" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Demander une Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              La Modernisation Opérationnelle au Service de Votre Croissance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous aidons les entreprises africaines à adopter des technologies modernes pour automatiser les processus manuels, connecter leurs systèmes et prendre de meilleures décisions basées sur les données
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-robot-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Automatisation</h3>
              <p className="text-gray-600">Réduisez les tâches manuelles répétitives</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-links-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Intégration</h3>
              <p className="text-gray-600">Connectez vos systèmes ensemble</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bar-chart-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Intelligence</h3>
              <p className="text-gray-600">Analysez vos données en temps réel</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brain-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">IA Moderne</h3>
              <p className="text-gray-600">Utilisez l'intelligence artificielle</p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Automation Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-robot-line text-2xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Automatisez Vos Processus Métier
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Éliminez les processus manuels répétitifs et libérez votre équipe pour des tâches à plus forte valeur ajoutée. Nos solutions d'automatisation transforment vos opérations quotidiennes.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20workflow%20automation%20dashboard%20showing%20automated%20business%20processes%20task%20management%20and%20process%20optimization%20interface%20professional%20technology%20system%20with%20flowcharts%20and%20automation%20rules&width=600&height=400&seq=workflow-automation-dash&orientation=landscape"
                alt="Workflow Automation"
                className="rounded-lg shadow-xl w-full h-auto object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-copy-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation Documentaire</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Génération automatique de documents</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Workflows d'approbation électronique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Archivage automatique et sécurisé</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Recherche intelligente de documents</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-mail-send-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation Communication</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Emails automatiques personnalisés</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Notifications SMS et WhatsApp</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rappels automatiques clients</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Campagnes marketing automatisées</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-task-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation des Tâches</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Attribution automatique des tâches</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Suivi automatique de progression</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Escalade automatique des retards</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rapports d'activité automatiques</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation Financière</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Facturation automatique récurrente</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Relances de paiement automatiques</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Réconciliation bancaire automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rapports financiers automatisés</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-archive-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation Inventaire</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Réapprovisionnement automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Alertes de stock bas automatiques</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Commandes fournisseurs automatiques</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Suivi automatique des mouvements</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-settings-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatisation RH</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Onboarding automatique employés</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Validation congés automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Calcul automatique de la paie</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Évaluations périodiques automatiques</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-purple-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Bénéfices de l'Automatisation</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Gain de Temps</h4>
                  <p className="text-sm text-gray-600">Réduisez jusqu'à 80% le temps passé sur les tâches répétitives</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-error-warning-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Moins d'Erreurs</h4>
                  <p className="text-sm text-gray-600">Éliminez les erreurs humaines dans les processus automatisés</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-money-dollar-circle-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Réduction des Coûts</h4>
                  <p className="text-sm text-gray-600">Optimisez vos ressources et réduisez les coûts opérationnels</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Integration Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="order-2 md:order-1">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20system%20integration%20dashboard%20showing%20connected%20software%20systems%20data%20flow%20and%20API%20integrations%20interface%20professional%20technology%20architecture%20with%20multiple%20connected%20platforms&width=600&height=400&seq=system-integration-dash&orientation=landscape"
                alt="System Integration"
                className="rounded-lg shadow-xl w-full h-auto object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-links-line text-2xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Connectez Vos Systèmes entre Eux
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Connectez tous vos systèmes logiciels ensemble pour créer un écosystème technologique unifié. Éliminez les silos de données et permettez à vos applications de communiquer efficacement.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-plug-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Intégrations API</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Connexion via API REST et SOAP</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Synchronisation en temps réel</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Webhooks et événements</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Authentification sécurisée</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-database-2-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Synchronisation Données</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Synchronisation bidirectionnelle</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Mapping de données automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Gestion des conflits</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Historique des synchronisations</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Intégration E-commerce</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Connexion boutiques en ligne</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Synchronisation produits et stocks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Import automatique commandes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Mise à jour statuts livraison</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bank-card-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Intégration Paiements</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Passerelles de paiement</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Mobile Money (MTN, Orange)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Réconciliation automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rapports de transactions</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Intégration Comptable</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Export automatique écritures</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Synchronisation factures</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rapprochement bancaire</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Conformité fiscale automatique</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-mail-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Intégration Communication</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Email marketing (Mailchimp, etc.)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>SMS et WhatsApp Business</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Réseaux sociaux</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Plateformes de messagerie</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-blue-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Avantages de l'Intégration</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-database-2-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Données Unifiées</h4>
                  <p className="text-sm text-gray-600">Une seule source de vérité pour toutes vos données métier</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-speed-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Efficacité Accrue</h4>
                  <p className="text-sm text-gray-600">Éliminez la double saisie et les transferts manuels</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Visibilité Totale</h4>
                  <p className="text-sm text-gray-600">Vue complète de vos opérations en temps réel</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Intelligence Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-bar-chart-line text-2xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Prenez de Meilleures Décisions grâce aux Données
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Transformez vos données en insights actionnables avec des tableaux de bord interactifs et des analyses avancées. Prenez des décisions éclairées basées sur des données en temps réel.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20business%20intelligence%20dashboard%20showing%20data%20analytics%20charts%20graphs%20KPIs%20and%20real-time%20metrics%20interface%20professional%20data%20visualization%20system%20with%20multiple%20interactive%20reports&width=600&height=400&seq=business-intelligence-dash&orientation=landscape"
                alt="Business Intelligence"
                className="rounded-lg shadow-xl w-full h-auto object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-dashboard-3-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tableaux de Bord Interactifs</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Visualisations en temps réel</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>KPIs personnalisables</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Graphiques interactifs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Filtres dynamiques</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyses Prédictives</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Prévisions de ventes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Analyse de tendances</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Détection d'anomalies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Modèles prédictifs</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-chart-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Rapports Automatisés</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Génération automatique de rapports</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Planification d'envoi</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Formats multiples (PDF, Excel)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Distribution automatique</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-funds-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyses Financières</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Analyse de rentabilité</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Suivi de trésorerie</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Analyse des coûts</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Budgets vs réalisations</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-heart-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyses Clients</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Segmentation clientèle</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Analyse comportementale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Valeur vie client (CLV)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Taux de rétention</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shopping-bag-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyses Opérationnelles</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Performance des ventes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Rotation des stocks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Productivité équipes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Efficacité processus</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-teal-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Pouvoir de la Business Intelligence</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-lightbulb-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Décisions Éclairées</h4>
                  <p className="text-sm text-gray-600">Basez vos décisions sur des données réelles et non sur l'intuition</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Visibilité Complète</h4>
                  <p className="text-sm text-gray-600">Vue à 360° de votre entreprise en temps réel</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-rocket-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Croissance Accélérée</h4>
                  <p className="text-sm text-gray-600">Identifiez rapidement les opportunités de croissance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Driven Tools Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="order-2 md:order-1">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20artificial%20intelligence%20AI%20dashboard%20showing%20machine%20learning%20automation%20predictive%20analytics%20and%20intelligent%20automation%20interface%20professional%20AI%20technology%20system%20with%20neural%20networks&width=600&height=400&seq=ai-driven-tools-dash&orientation=landscape"
                alt="AI-Driven Tools"
                className="rounded-lg shadow-xl w-full h-auto object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                L'Intelligence Artificielle au Service de Votre Activité
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Exploitez la puissance de l'intelligence artificielle moderne pour automatiser les tâches complexes, générer des insights avancés et améliorer continuellement vos opérations.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-robot-2-line text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Assistants IA</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Chatbots intelligents 24/7</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Assistants vocaux</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Support client automatisé</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Réponses contextuelles</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Traitement Intelligent Documents</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>OCR et extraction de données</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Classification automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Validation intelligente</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Archivage automatique</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyses Prédictives IA</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Prévisions de demande</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Détection de fraude</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Prédiction de churn</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Optimisation des prix</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-search-line text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Personnalisation IA</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Recommandations personnalisées</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Contenu dynamique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Ciblage intelligent</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Expérience utilisateur adaptative</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-image-line text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Vision par Ordinateur</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Reconnaissance d'images</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Détection d'objets</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Contrôle qualité automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Surveillance intelligente</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-translate-2 text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Traitement du Langage</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Analyse de sentiment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Résumé automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Traduction automatique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Extraction d'informations</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-orange-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Impact de l'IA sur Votre Entreprise</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-rocket-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Innovation Continue</h4>
                  <p className="text-sm text-gray-600">L'IA apprend et s'améliore continuellement avec vos données</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-flashlight-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Automatisation Intelligente</h4>
                  <p className="text-sm text-gray-600">Automatisez même les tâches complexes nécessitant du jugement</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-trophy-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Avantage Compétitif</h4>
                  <p className="text-sm text-gray-600">Dépassez vos concurrents avec des capacités IA avancées</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Prêt à Moderniser Vos Opérations ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Découvrez comment nos solutions d'automatisation et de digitalisation peuvent transformer votre entreprise
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/237693385267" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              Demander une Consultation
            </a>
            <a 
              href="/solutions" 
              className="bg-white text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir Toutes Nos Solutions
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}